import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Text,
} from "flicket-ui";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  BaseContext,
  ReservationActionTypes,
  ReservationContext,
} from "~context";
import {
  EventQuery,
  FeeType,
  Integration,
  MembershipQuery,
  MembershipTypeKind,
  Permission,
  TicketTypeKind,
} from "~graphql/sdk";
import { useAccount, usePermissions } from "~hooks";
import { OrganizationFeatures } from "~lib";
import Laybuy from "../common/common.Laybuy";
import { getTicketFeeProperties } from "../common/common.utils";
import { AddonListItem, ListItem } from "./item";

type EventOrMembership = EventQuery["event"] | MembershipQuery["membership"];

type PricingProps = {
  bookingFee: EventOrMembership["bookingFee"];
  bookingFeeType: EventOrMembership["bookingFeeType"];
  addons?: any[];
  ticketTypes: any;
  availableTicketTypes: any;
  changingSeatsFee?: number;
  changeTickets: any[];
  navigate: () => void;
  laybuyGateway: Partial<Integration>;
  isNavigating?: boolean;
  getPrice: (seat: any) => number;
  type?: "membership" | "event";
  noSeatsAvailable: boolean;
  onBack: () => void;
  priceComp: number | undefined;
  allowPublicComp?: boolean;
  validSeatSelection: boolean;
};

export const Pricing = ({
  bookingFee,
  bookingFeeType,
  addons: addonOptions,
  ticketTypes,
  availableTicketTypes,
  changingSeatsFee,
  changeTickets,
  navigate,
  laybuyGateway,
  isNavigating,
  getPrice,
  type,
  noSeatsAvailable,
  onBack,
  priceComp,
  allowPublicComp,
  validSeatSelection,
}: PricingProps) => {
  const { seats, addons, dispatch } = useContext(ReservationContext);
  const checkPermissions = usePermissions();
  const [totalTickets, setTotalTickets] = useState(0);
  const { isPOS, isAdmin } = useAccount();

  const { organization } = useContext(BaseContext);

  const showHiddenFees = !!organization.features?.includes(
    OrganizationFeatures.ShowHiddenFees
  );

  const ticketingOptions = useMemo(() => {
    let filteredTicketTypes =
      (isAdmin && checkPermissions(Permission.OrderComp)) || allowPublicComp
        ? ticketTypes
        : type === "membership"
        ? ticketTypes?.filter(
            ({ kind }) => kind === MembershipTypeKind.Standard
          )
        : ticketTypes?.filter(({ kind }) => kind === TicketTypeKind.Standard);

    if (availableTicketTypes?.length) {
      filteredTicketTypes = filteredTicketTypes?.filter(
        (ticketType) =>
          !!availableTicketTypes?.find(
            ({ id, ticketTypeId, membershipTypeId, isActive }) =>
              (ticketType?.id === id ||
                ticketType?.id === ticketTypeId ||
                ticketType?.id === membershipTypeId) &&
              (typeof isActive !== "undefined" ? isActive : true)
          )
      );
    }

    return filteredTicketTypes?.length
      ? filteredTicketTypes.map(({ id, name, ...rest }) => ({
          label: name.replace(/ticket/i, ""),
          value: id,
          ...rest,
        }))
      : [];
  }, [ticketTypes, availableTicketTypes, isAdmin]);

  const availableAddons = useMemo(
    () => addonOptions?.filter(({ isActive }) => isActive),
    [addonOptions]
  );

  const total = useMemo(() => {
    let total = 0;

    seats?.forEach((seat) => {
      let price = getPrice(seat);

      const {
        ticketFee,
        ticketFeeType,
        stadiumLevyFee,
      } = getTicketFeeProperties(seat.ticketType);

      if (showHiddenFees && ticketFee > 0) {
        if (ticketFeeType === FeeType.Percentage) {
          price *= ticketFee / 100;
        } else {
          price += ticketFee;
        }
      }

      total += price + stadiumLevyFee;
    });

    availableAddons?.forEach((addon) => {
      const quantity = addons?.[addon.id]?.value;

      if (quantity) {
        total += addon.price * quantity;
      }
    });

    if (priceComp) {
      total -= priceComp;

      if (changingSeatsFee) {
        total += changingSeatsFee;
      }
    }

    if (showHiddenFees && bookingFee > 0) {
      if (bookingFeeType === FeeType.Percentage) {
        total *= bookingFee / 100;
      } else {
        total += bookingFee;
      }
    }

    return total;
  }, [
    seats,
    availableAddons,
    addons,
    changingSeatsFee,
    changeTickets,
    getPrice,
  ]);

  useEffect(() => {
    seats ? setTotalTickets(seats.length) : setTotalTickets(0);
  }, [seats]);

  // update seat with reducer here
  const updateSeat = useCallback(
    (id, newType) =>
      dispatch({
        type: ReservationActionTypes.SET_SEAT_TYPE,
        payload: { id, ticketType: newType },
      }),
    [dispatch]
  );

  const updateAddon = useCallback(
    (id, quantity) =>
      dispatch({
        type: ReservationActionTypes.UPDATE_ADDON,
        payload: { id, quantity },
      }),
    [dispatch]
  );

  let pricingOptions;

  if (seats?.length) {
    pricingOptions = seats?.map((seat, i) => (
      <ListItem
        seat={seat}
        handleTicketTypeChange={updateSeat}
        options={ticketingOptions}
        getPrice={getPrice}
        key={i}
        showHiddenFees={showHiddenFees}
      />
    ));
  } else if (type === "membership") {
    pricingOptions = <Text mb={3}>Please select your seating</Text>;
  } else if (noSeatsAvailable) {
    pricingOptions = (
      <>
        <Text mb={3}>Not enough seats available in this zone</Text>
        <PrimaryButton
          onClick={onBack}
          boxShadow="button"
          mt={{ _: "6/4", md: 1 }}
          w="100%"
        >
          Clear selection
        </PrimaryButton>
      </>
    );
  } else {
    pricingOptions = <Spinner size={48} color="P300" mb={2} />;
  }

  const totalPrice = total;

  return (
    <Box>
      <Text fontWeight="heavy" fontSize={6} color="N800" lineHeight="normal">
        Your selection
      </Text>

      <Divider my={2} />

      {pricingOptions}

      {!noSeatsAvailable && (seats?.length || type === "membership") && (
        <>
          {!!availableAddons?.length && (
            <Box mb={2} mt={4}>
              <Text
                fontWeight="heavy"
                fontSize={6}
                color="N800"
                lineHeight="normal"
              >
                Available Addons
              </Text>
              <Divider my={2} />
              {availableAddons.map(({ id, name, price, ...rest }) => (
                <AddonListItem
                  {...rest}
                  key={id}
                  name={name}
                  price={price}
                  quantity={addons?.[id] || 0}
                  onChange={(value) => {
                    updateAddon(id, value);
                  }}
                />
              ))}
            </Box>
          )}

          {!!changeTickets?.length && (
            <Box mb={2}>
              <Divider />
              {!!changingSeatsFee && (
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <Text fontWeight="extraBold" fontSize={3} color="N700">
                    Reservation change fee
                  </Text>
                  <Text fontWeight="medium" fontSize={2} color="N600">
                    <Price price={changingSeatsFee} />
                  </Text>
                </Flex>
              )}
              {!!priceComp && (
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <Text fontWeight="extraBold" fontSize={3} color="N700">
                    Price difference compensation
                  </Text>
                  <Text fontWeight="medium" fontSize={2} color="N600">
                    <Price price={-priceComp} />
                  </Text>
                </Flex>
              )}
            </Box>
          )}

          <Divider />

          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total tickets
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {totalTickets}
            </Text>
          </Flex>

          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total {showHiddenFees ? "(including booking fees)" : ""}
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {seats ? <Price price={totalPrice} /> : "-"}
            </Text>
          </Flex>

          {laybuyGateway && !isPOS && (
            <Box mt={2} w="100%">
              <Laybuy
                totalPrice={totalPrice}
                hasTicketSelected={!!seats}
                showHiddenFees={showHiddenFees}
                isSmallDisplay={true}
                laybuy={laybuyGateway}
              />
            </Box>
          )}

          <PrimaryButton
            boxShadow="button"
            mt={{ _: "6/4", md: 4 }}
            onClick={navigate}
            isLoading={isNavigating}
            disabled={
              !seats?.length ||
              (validSeatSelection === false && !isAdmin && !isPOS)
            }
            w="100%"
            fontSize={4}
          >
            {validSeatSelection === true || isAdmin || isPOS
              ? type === "event"
                ? "Buy tickets"
                : "Buy memberships"
              : "Can not leave a single seat"}
          </PrimaryButton>
        </>
      )}
    </Box>
  );
};
